import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectSection, Task, TaskInput } from '@stromo/shared';
import { api } from './api';
import { useUndoStore } from './undoStore';

/** Jede Mutation an Sections/Tasks beeinflusst sowohl die Detailansicht
 *  als auch den Gantt (Termine, Status, Auslastung) — daher zentral. */
function invalidateProjectAndGantt(
  qc: ReturnType<typeof useQueryClient>,
  projectId: number,
  sectionId?: number,
) {
  qc.invalidateQueries({ queryKey: ['sections', projectId] });
  qc.invalidateQueries({ queryKey: ['project', projectId] });
  qc.invalidateQueries({ queryKey: ['gantt'] });
  if (sectionId !== undefined) {
    qc.invalidateQueries({ queryKey: ['tasks', sectionId] });
  }
}

export function useSections(projectId: number | undefined) {
  return useQuery({
    queryKey: ['sections', projectId],
    queryFn: async () => {
      const { data } = await api.get<{ items: ProjectSection[] }>(`/projects/${projectId}/sections`);
      return data.items;
    },
    enabled: projectId !== undefined,
  });
}

export type CreateSectionInput =
  | { mode: 'empty'; name: string }
  | { mode: 'duplicate'; name: string; source_section_id: number }
  | { mode: 'from_template'; name: string; template_id: number };

export function useCreateSection(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSectionInput) => {
      const { data } = await api.post<ProjectSection>(
        `/projects/${projectId}/sections`,
        input,
      );
      return data;
    },
    onSuccess: () => invalidateProjectAndGantt(qc, projectId),
  });
}

export function useRenameSection(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const { data } = await api.put<ProjectSection>(`/sections/${id}`, { name });
      return data;
    },
    onSuccess: () => invalidateProjectAndGantt(qc, projectId),
  });
}

export function useDeleteSection(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/sections/${id}`);
      return id;
    },
    onSuccess: (id) => {
      invalidateProjectAndGantt(qc, projectId);
      useUndoStore.getState().show({
        label: 'Sektion gelöscht',
        onUndo: async () => {
          try {
            await api.post(`/sections/${id}/restore`);
            invalidateProjectAndGantt(qc, projectId);
          } catch {
            /* Restore-Fehler ignorieren — Toast ist eh weg */
          }
        },
      });
    },
  });
}

export function useTasks(sectionId: number | undefined) {
  return useQuery({
    queryKey: ['tasks', sectionId],
    queryFn: async () => {
      const { data } = await api.get<{ items: Task[] }>(`/sections/${sectionId}/tasks`);
      return data.items;
    },
    enabled: sectionId !== undefined,
  });
}

export interface CreateTaskInput {
  input: TaskInput;
  /** Wenn gesetzt: neue Task direkt nach dieser Task einfügen statt am Ende anhängen. */
  afterTaskId?: number;
}

export function useCreateTask(sectionId: number, projectId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: TaskInput | CreateTaskInput) => {
      // Rückwärtskompatibel: Aufruf mit nacktem TaskInput weiter erlaubt.
      const isWrapped = (a: TaskInput | CreateTaskInput): a is CreateTaskInput =>
        typeof a === 'object' && a !== null && 'input' in a;
      const body = isWrapped(args)
        ? { ...args.input, ...(args.afterTaskId !== undefined ? { after_task_id: args.afterTaskId } : {}) }
        : args;
      const { data } = await api.post<Task>(`/sections/${sectionId}/tasks`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', sectionId] });
      qc.invalidateQueries({ queryKey: ['gantt'] });
      if (projectId !== undefined) {
        qc.invalidateQueries({ queryKey: ['project', projectId] });
      }
    },
  });
}

export function useReorderTasks(sectionId: number, projectId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: number[]) => {
      await api.post(`/sections/${sectionId}/tasks/reorder`, { order: orderedIds });
      return orderedIds;
    },
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: ['tasks', sectionId] });
      const prev = qc.getQueryData<Task[]>(['tasks', sectionId]);
      if (prev) {
        const byId = new Map(prev.map((t) => [t.id, t]));
        const next = orderedIds
          .map((id, idx) => {
            const t = byId.get(id);
            return t ? { ...t, sort_order: idx } : null;
          })
          .filter((t): t is Task => t !== null);
        qc.setQueryData<Task[]>(['tasks', sectionId], next);
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) qc.setQueryData(['tasks', sectionId], context.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tasks', sectionId] });
      qc.invalidateQueries({ queryKey: ['gantt'] });
      if (projectId !== undefined) {
        qc.invalidateQueries({ queryKey: ['project', projectId] });
      }
    },
  });
}

export function useUpdateTask(sectionId: number, projectId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<TaskInput> }) => {
      const { data } = await api.put<Task>(`/tasks/${id}`, patch);
      return data;
    },
    onMutate: async ({ id, patch }) => {
      // optimistic update lokal in der Tasks-Liste
      await qc.cancelQueries({ queryKey: ['tasks', sectionId] });
      const prev = qc.getQueryData<Task[]>(['tasks', sectionId]);
      if (prev) {
        qc.setQueryData<Task[]>(['tasks', sectionId], prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) qc.setQueryData(['tasks', sectionId], context.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tasks', sectionId] });
      qc.invalidateQueries({ queryKey: ['gantt'] });
      if (projectId !== undefined) {
        qc.invalidateQueries({ queryKey: ['project', projectId] });
      }
    },
  });
}

export function useDeleteTask(sectionId: number, projectId?: number) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks', sectionId] });
    qc.invalidateQueries({ queryKey: ['gantt'] });
    if (projectId !== undefined) {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    }
  };
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/tasks/${id}`);
      return id;
    },
    onSuccess: (id) => {
      invalidate();
      useUndoStore.getState().show({
        label: 'Aufgabe gelöscht',
        onUndo: async () => {
          try {
            await api.post(`/tasks/${id}/restore`);
            invalidate();
          } catch {
            /* silent */
          }
        },
      });
    },
  });
}
